<template>
  <div class="start-audit-view">
    <div class="audit-container">
      <div class="view-header">
        <div class="icon-wrapper">
          <i class="pi pi-qrcode"></i>
        </div>
        <h1 class="view-title">Start Audit</h1>
        <p class="view-description">Scan a RapID tag barcode to begin the audit process</p>
      </div>

      <div class="scanner-section">
        <BarcodeScanner ref="barcodeScannerRef" @scan="handleScan" @error="handleScanError" />
      </div>

      <div v-if="isLoading" class="loading-status">
        <div class="spinner"></div>
        <span>{{ loadingMessage }}</span>
      </div>

      <div v-if="error" class="error-banner"><strong>Error:</strong> {{ error }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuditStore } from '../../stores/audit.store';
import { useAuthStore } from '../../stores/auth.store';
import { scanAnimal } from '../../services/audit.service';
import { isUnauthorizedError } from '../../services/api-error.service';
import { redirectToLogin } from '../../services/auth-redirect.service';
import { useAuditSSE } from '../../composables/useAuditSSE';
import BarcodeScanner from '../../components/audit/BarcodeScanner.vue';

const router = useRouter();
const auditStore = useAuditStore();
const authStore = useAuthStore();
const barcodeScannerRef = ref<InstanceType<typeof BarcodeScanner> | null>(null);
const isLoading = ref(false);
const localError = ref<string | null>(null);
const correlationId = ref<string | null>(null);
const { connect } = useAuditSSE(correlationId);
const hasNavigated = ref(false); // Prevent multiple navigations

// Combine local error with store error (from SSE timeout)
const error = computed(() => localError.value || auditStore.error);
const loadingMessage = computed(() => auditStore.lookupProgressMessage || 'Processing scan...');

/**
 * Watch for cageInfo to be populated and navigate automatically
 */
watch(
  () => auditStore.cageInfo,
  (cageInfo) => {
    // Navigate to cage verification when cageInfo is populated with a cage number
    if (cageInfo?.number && !hasNavigated.value && correlationId.value) {
      console.info('Cage info populated, navigating to cage verification', {
        cageId: cageInfo.id,
        cageNumber: cageInfo.number,
      });
      hasNavigated.value = true;
      isLoading.value = false;
      router.push({ name: 'audit-cage-verification' });
    }
  },
  { immediate: false },
);

/**
 * Watch for store errors (e.g., from SSE timeout) and stop loading
 */
watch(
  () => auditStore.error,
  (storeError) => {
    if (storeError) {
      isLoading.value = false;
      // Reset the scanner so user can try again
      if (barcodeScannerRef.value) {
        barcodeScannerRef.value.reset();
      }
    }
  },
);

/**
 * Handle barcode scan
 */
async function handleScan(barcode: string): Promise<void> {
  // Clear any previous errors (both local and store)
  localError.value = null;
  auditStore.setError(null);
  auditStore.setLookupProgress('publishing-scan');
  isLoading.value = true;
  hasNavigated.value = false; // Reset navigation flag for new scan

  try {
    const token = authStore.accessToken;
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }

    const response = await scanAnimal(barcode, token);
    correlationId.value = response.correlationId;
    auditStore.setLookupProgress('scan-accepted');

    // Connect to SSE to receive lookup completion event
    // The watcher will automatically navigate when cageInfo is populated
    connect();
  } catch (err) {
    if (isUnauthorizedError(err)) {
      authStore.clearAuth();
      await redirectToLogin(router, router.currentRoute.value.fullPath);

      return;
    }

    localError.value = err instanceof Error ? err.message : 'Failed to process scan';
    auditStore.setLookupProgress('idle');
    isLoading.value = false;
    if (barcodeScannerRef.value) {
      barcodeScannerRef.value.reset();
    }
  }
}

/**
 * Handle scanner errors
 */
function handleScanError(message: string): void {
  localError.value = message;
  auditStore.setLookupProgress('idle');
  isLoading.value = false;
}

/**
 * Focus scanner on mount
 */
onMounted(async () => {
  // Reset audit store when starting new audit (clears errors too)
  auditStore.reset();
  localError.value = null;
  hasNavigated.value = false; // Reset navigation flag

  await nextTick();
  setTimeout(() => {
    if (barcodeScannerRef.value) {
      barcodeScannerRef.value.focus();
    }
  }, 100);
});
</script>

<style scoped>
.start-audit-view {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 0;
  padding: 1rem;
  background-color: #f9fafb;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.audit-container {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.view-header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  margin-bottom: 0.5rem;
}

.view-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.view-description {
  color: #6b7280;
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
}

.scanner-section {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.loading-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  color: #3b82f6;
}

.spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-banner {
  padding: 1rem;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #991b1b;
}

@media (max-width: 768px) {
  .audit-container {
    padding: 1.5rem;
  }

  .view-title {
    font-size: 1.75rem;
  }
}
</style>
